import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, Card, Input, OverflowMenu, PageLoader, Typography, type OverflowMenuAction } from '../ui';
import { useBasePath } from '../../../lib/use-base-path';
import { useContactBook, type ContactId, type WebContact, type WebContactCard } from '../../../lib/contact-book';
import {
  channelLabelKey,
  contactDisplayName,
  inboxContactOptions,
  groupInboxRowsByStage,
  inboxResultSummary,
  inboxRows,
  inboxRowMatchesSearch,
  isEmptyBook,
} from '../../../lib/contact-rows';
import { STAGE_GROUPS, stageGroup, type StageGroupId } from '../../../lib/outreach-stage';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useSearchStartedBi } from '../../../lib/use-search-started-bi';
import { useTranslation } from '../../../lib/i18n';
import { ContactOutreachViewer } from './ContactOutreachViewer';
import { leadNamesByOutreach, unnamedLabelFor, type PeopleLeadRow } from '../../../lib/profile-links-model';
import { InboxRow } from './InboxRow';
import type { ContactsListState } from './ContactsBody';
import { ContactReadError } from './ContactReadError';
import { CategoryList, CategoryListGroupHeader } from '../category-view/CategoryView';
import styles from './contacts.module.css';

/**
 * The **Inbox** list — one row per OUTREACH (`inboxRows`), grouped by STAGE (`STAGE_GROUPS`: Needs you ·
 * In progress · Done), each newest-first and Done by when it closed. An empty group is not drawn — so
 * "Needs you" disappears when nothing needs the owner — while the Status filter always offers all three.
 * A row opens that conversation in the outreach viewer, exactly like the Contacts list.
 *
 * A "Channel" filter sits on the first section header — it opens a dialog/sheet to filter the whole
 * list to one channel, with "All channels" to clear. The empty BOOK (no correspondence at all) shows
 * the shared illustrated empty state. Shared 1:1 with iOS/Android `OutreachScreen` inbox.
 *
 * Search ownership is per caller, like `ContactsBody`: the standalone Outreach screen controls the
 * query so it can draw the field in its desktop toolbar; the body draws it full-width on mobile-web.
 */
export function InboxBody({
  onOpen,
  emptyAction,
  emptyState,
  onListState,
  search: controlledSearch,
  onSearchChange,
  renderSearch = true,
  filtersSlot,
  profileLeads,
}: {
  /** Open the contact SCREEN by identity — the viewer's header uses it for the full record. */
  onOpen: (id: ContactId) => void;
  /** The empty-state CTA rendered inside the illustrated card (desktop); narrow pins its own foot CTA. */
  emptyAction?: ReactNode;
  /** Drawn in place of the illustrated card — the home's designed empty state. */
  emptyState?: ReactNode;
  onListState?: (state: ContactsListState) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
  renderSearch?: boolean;
  /** The host's toolbar slot (desktop, Figma 21055:254883): Channel · Contact · Status render there, beside
   *  the search, instead of in a row of their own above the list. Their state stays here, with the rows. */
  filtersSlot?: HTMLElement | null;
  /** The owner's profile-link leads (`useProfileLinkLeadRows`): they name the rows the book names nobody on. */
  profileLeads?: readonly PeopleLeadRow[];
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  // A profile-link visitor whose account has no name: the lead names the row by its link ("Visitor from Wix 3.0").
  const unnamedLabel = t('contacts.row.unnamed');
  const leadNames = useMemo(
    () => leadNamesByOutreach(profileLeads ?? [], (link) => t('profileLinks.leads.visitorFrom', { link }), t('profileLinks.main')),
    [profileLeads, t],
  );
  const { logPageClickBi } = useGenericBi();
  // A row IS one outreach, and it opens THAT conversation in the viewer — not, as before, every
  // outreach on its channel merged into one scroll.
  const [viewer, setViewer] = useState<{ personId: string; recipientId?: string; outreachId: string } | null>(null);
  const openOutreach = (personId: string, recipientId: string | undefined, outreachId: string) =>
    setViewer({ personId, recipientId, outreachId });

  const [internalSearch, setInternalSearch] = useState('');
  const search = controlledSearch ?? internalSearch;
  const setSearch = onSearchChange ?? setInternalSearch;
  // Fire `outreach_search` once on the query's blank→non-blank transition (see ContactsBody — only the
  // active tab's body is mounted, so exactly one of the two watchers reports).
  useSearchStartedBi(search, 'inbox');
  const [channel, setChannel] = useState<string | null>(null);
  // Two more axes beside the channel. All three are CLIENT-side for the reason the file's header
  // already gives about the chips: the endpoint takes no filter, so a server-side one could only trim
  // the page it had already fetched and would report "none" for a row that sat outside it.
  const [contactKey, setContactKey] = useState<string | null>(null);
  const [status, setStatus] = useState<StageGroupId | null>(null);

  // The full book, fetched once; search filters it client-side (the server people-search misses
  // existing contacts) — the same read `ContactsBody` uses, so the two tabs share one cache entry.
  const state = useContactBook('');
  const entries = useMemo(() => (state.kind === 'ready' ? state.contacts : []), [state]);
  // Build every row from the whole book, then filter PER ROW on the name/label OR the shown last
  // message — so a search hits one channel of a person by what was said, without pulling in their
  // other channels. (Contacts-tab search stays name/label only, on its own body.)
  const built = useMemo(() => inboxRows<WebContactCard>(entries as WebContact[]), [entries]);
  const bookRowCount = built.length;
  const searched = useMemo(() => built.filter((row) => inboxRowMatchesSearch(row, search)), [built, search]);

  // Every channel present (before the channel filter narrows the rows), so the filter can offer any of them.
  const channels = useMemo(() => {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const row of searched) {
      // An empty channel is UNKNOWN (`threadChannel`): it wears no chip, so it is not an option either —
      // a blank entry in the menu would filter to rows that name no channel at all.
      if (row.channel && !seen.has(row.channel)) {
        seen.add(row.channel);
        order.push(row.channel);
      }
    }
    return order;
  }, [searched]);

  // Every contact present in the rows, in first-seen order, so the filter offers exactly the people
  // this inbox actually holds — not the whole book, most of which has no correspondence.
  // Through the shared rule, which also DISAMBIGUATES: two different people who are both called
  // "Dana Levi" would otherwise render two identical options nobody could choose between.
  const contacts = useMemo(() => inboxContactOptions(searched, t('contacts.row.unnamed')), [searched, t]);

  const matches = (row: (typeof searched)[number]) =>
    (!channel || row.channel === channel) &&
    (!contactKey || (row.personId || row.recipientId) === contactKey) &&
    (!status || stageGroup(row.stage) === status);
  // Grouped by stage, through the SAME `STAGE_GROUPS` the contact screen labels its rows with — so the two
  // screens cannot end up with different groups, a different order, or different headings.
  const grouped = useMemo(() => groupInboxRowsByStage(searched.filter(matches)), [searched, channel, contactKey, status]);
  const total = grouped.reduce((n, { items }) => n + items.length, 0);
  // "1 match for “…”" + Clear, whenever the query OR any filter narrows the list. The query is restored
  // from the URL (`iq`), so without this a stale search reads as conversations gone missing — an owner saw
  // "Done · 1" and one row, with an old query still sitting in the field.
  const summary =
    state.kind === 'ready' && bookRowCount > 0 ? inboxResultSummary(search, !!channel || !!contactKey || !!status, total) : null;
  const clearAll = () => {
    logPageClickBi({ page_name: 'contacts_page', button_name: 'inbox_results_clear' });
    setSearch('');
    setChannel(null);
    setContactKey(null);
    setStatus(null);
  };

  // The illustrated empty state is for a truly empty BOOK (no correspondence at all) — measured on the
  // unfiltered rows, so a search that matches nothing falls to the "no results" line instead.
  const richEmpty = (!!emptyAction || renderSearch) && isEmptyBook(state.kind === 'ready', bookRowCount, false);
  const listState: ContactsListState =
    state.kind === 'loading' ? 'loading' : state.kind === 'error' ? 'error' : richEmpty ? 'empty' : 'ready';
  useEffect(() => {
    onListState?.(listState);
  }, [listState, onListState]);

  const showSearch = renderSearch && state.kind !== 'loading' && !richEmpty;

  const channelLabel = (value: string) => {
    const key = channelLabelKey(value);
    return key ? t(key) : value;
  };
  // The channel filter KEEPS its original look — a quiet text button reading "Channel" (the channel
  // name once picked) with a chevron, on the section-header end — but the OPENED menu is now the SAME
  // dropdown the Files screen filters use: the DS `OverflowMenu` labelled picker (`triggerVariant="text"`),
  // a portal list with a check on the selection and "All channels" first to clear. Not a modal/sheet.
  const channelActions: OverflowMenuAction[] = [
    {
      key: 'any',
      label: t('outreach.inbox.filter.allChannels'),
      onSelect: () => {
        logPageClickBi({ page_name: 'contacts_page', button_name: 'inbox_filter_channel_all' });
        setChannel(null);
      },
    },
    ...channels.map((value) => ({
      key: value,
      label: channelLabel(value),
      onSelect: () => {
        // Capture WHICH channel was picked (the name-only event couldn't tell them apart).
        logPageClickBi(
          { page_name: 'contacts_page', button_name: 'inbox_filter_channel' },
          { additionalFields: { channel: value } },
        );
        setChannel(value);
      },
    })),
  ];
  const contactActions: OverflowMenuAction[] = [
    {
      key: 'any',
      label: t('contacts.section.all'),
      onSelect: () => {
        logPageClickBi({ page_name: 'contacts_page', button_name: 'inbox_filter_contact_all' });
        setContactKey(null);
      },
    },
    ...contacts.map(({ key, label }) => ({
      key,
      label,
      onSelect: () => {
        // The contact's NAME never rides the event — a display name is user data. Only that one was
        // picked, which is all the funnel needs.
        logPageClickBi({ page_name: 'contacts_page', button_name: 'inbox_filter_contact' });
        setContactKey(key);
      },
    })),
  ];

  const statusActions: OverflowMenuAction[] = [
    {
      key: 'any',
      label: t('outreach.inbox.section.all'),
      onSelect: () => {
        logPageClickBi({ page_name: 'contacts_page', button_name: 'inbox_filter_status_all' });
        setStatus(null);
      },
    },
    // Straight off `STAGE_GROUPS`, so the filter offers the three groups in the order the list draws them —
    // ALWAYS all three, even one that is empty right now. The BI value is the group's own (`needs_you`,
    // `in_progress`, `done`), pinned in the shared contract.
    ...STAGE_GROUPS.map(({ id, labelKey, bi }) => ({
      key: id,
      label: t(labelKey),
      onSelect: () => {
        logPageClickBi({ page_name: 'contacts_page', button_name: 'inbox_filter_status' }, { additionalFields: { status: bi } });
        setStatus(id);
      },
    })),
  ];

  const selectedContactLabel = contacts.find((c) => c.key === contactKey)?.label;
  const filterButton = (
    <span className={styles.inboxFilters}>
      <OverflowMenu
        label={channel ? channelLabel(channel) : t('outreach.inbox.channelFilter')}
        ariaLabel={t('outreach.inbox.channelFilter')}
        actions={channelActions}
        selectedKey={channel ?? 'any'}
        triggerVariant="text"
        triggerSize="small"
        className={styles.channelFilter}
      />
      <OverflowMenu
        label={selectedContactLabel ?? t('contacts.detail.title')}
        ariaLabel={t('contacts.detail.title')}
        actions={contactActions}
        selectedKey={contactKey ?? 'any'}
        triggerVariant="text"
        triggerSize="small"
        className={styles.channelFilter}
      />
      <OverflowMenu
        label={status ? t(STAGE_GROUPS.find((g) => g.id === status)?.labelKey ?? '') : t('outreach.inbox.statusFilter')}
        ariaLabel={t('outreach.inbox.statusFilter')}
        actions={statusActions}
        selectedKey={status ?? 'any'}
        triggerVariant="text"
        triggerSize="small"
        className={styles.channelFilter}
      />
    </span>
  );

  // One group of the list card: its header row ("In progress · 2") and its rows. The count stays
  // OUT of the i18n string — a number and a middot need no translation, and no key had to change.
  const section = (labelKey: string, rows: typeof searched) =>
    // Keyed by the section's own label so React keeps the three groups distinct across re-renders.
    rows.length > 0 ? (
      <Fragment key={labelKey}>
        <CategoryListGroupHeader>{`${t(labelKey)} · ${rows.length}`}</CategoryListGroupHeader>
        {rows.map((row) => (
          <InboxRow
            // The OUTREACH, not `person:channel` — a channel now carries several rows, so that key
            // stopped being unique the moment the list went flat. A duplicate key makes React
            // mis-reconcile rows (and silently drops one on the SwiftUI port).
            key={row.outreachId}
            row={row}
            onOpen={openOutreach}
            unnamedLabel={unnamedLabelFor([row.outreachId], leadNames, unnamedLabel)}
          />
        ))}
      </Fragment>
    ) : null;
  const sections = grouped.map(({ group, items }) => section(group.labelKey, items)).filter(Boolean);

  return (
    <div className={styles.body}>
      {showSearch ? (
        <div className={styles.search}>
          <Input
            kind="search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
            clearAriaLabel={t('search.dialog.clear')}
            placeholder={t('outreach.inbox.search.placeholder')}
          />
        </div>
      ) : null}

      {state.kind === 'loading' ? <PageLoader /> : null}

      {state.kind === 'error' ? (
        <ContactReadError canRetry onRetry={state.retry} detail={state.message} fullHeight={false} />
      ) : null}

      {/* The filters sit ABOVE the empty state, not below it, and above the sections rather than on the
          first section's header. Both placements are the same requirement: a section renders nothing at
          zero rows, so filters inside one DISAPPEAR on the empty result they caused — and filters that
          render UNDER the "nothing found" line read as belonging to it rather than as the controls that
          produced it. With three ANDed axes an empty result is ordinary, not an edge case, so the bar
          stays exactly where it is at every row count and the reader widens back out from the same
          place they narrowed. */}
      {state.kind === 'ready' && bookRowCount > 0
        ? filtersSlot
          ? createPortal(filterButton, filtersSlot)
          : <div className={styles.inboxFilterBar}>{filterButton}</div>
        : null}

      {summary ? (
        // One line, above the results (and above the no-match line, so its Clear is the way out of it too).
        // `role="status"` so a screen reader hears the count change as the query narrows.
        <div className={styles.inboxResults} role="status">
          <Typography variant="p3" color="secondary" className={styles.inboxResultsText} dir="auto">
            {t(summary.key, { count: summary.count, query: summary.query })}
          </Typography>
          <Button variant="text" size="small" onClick={clearAll}>
            {t('outreach.inbox.results.clear')}
          </Button>
        </div>
      ) : null}

      {richEmpty && emptyState ? (
        emptyState
      ) : richEmpty ? (
        <Card className={styles.emptyCard}>
          <img src={`${basePath}/illustrations/outreach-empty.svg`} alt="" className={styles.emptyIllustration} />
          <div className={styles.emptyText}>
            <Typography variant="h4" color="strong">
              {t('outreach.inbox.empty.title')}
            </Typography>
            <Typography variant="p3" color="secondary" className={styles.emptyBody}>
              {t('outreach.inbox.empty.body')}
            </Typography>
          </div>
          {emptyAction}
        </Card>
      ) : state.kind === 'ready' && total === 0 ? (
        // The book has correspondence, but this search + the three filters matched none of it. The
        // filter bar above stays mounted, so the reader can widen back out from here.
        //
        // Its OWN copy, not the Contacts tab's. That one reads "Nobody by that name" over "Search
        // looks through everyone your agents know, not only the people you've messaged" — wrong twice
        // here: the list can be emptied by a status or channel with nothing typed at all, and the
        // Inbox does the OPPOSITE of what the body promises (it filters conversations already loaded,
        // it does not reach into the people book). This names the unit the Inbox lists and the way out.
        <div className={styles.empty}>
          <Typography variant="p2">{t('outreach.inbox.noMatch.title')}</Typography>
          <Typography variant="p3" color="secondary">
            {t('outreach.inbox.noMatch.body')}
          </Typography>
        </div>
      ) : null}

      {sections.length > 0 ? <CategoryList>{sections}</CategoryList> : null}

      {viewer ? (
        <ContactOutreachViewer
          personId={viewer.personId}
          recipientId={viewer.recipientId}
          outreachId={viewer.outreachId}
          open
          onClose={() => setViewer(null)}
          // The header opens the contact SCREEN by the same identity the row carried — person, or
          // (address-only) recipient.
          onOpenContact={() => {
            const id: ContactId = viewer.personId ? { personId: viewer.personId } : { recipientId: viewer.recipientId ?? '' };
            setViewer(null);
            onOpen(id);
          }}
        />
      ) : null}
    </div>
  );
}
